import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, Hotel, Car, Package, User, Menu, X, LayoutDashboard, LogOut, Ticket, Phone, ShieldCheck, ChevronDown, Clock, Headphones, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { COLLECTION_NAV } from "@/data/collections";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/flights", label: "Flights", icon: Plane },
  { to: "/hotels", label: "Hotels", icon: Hotel },
  { to: "/packages", label: "Packages", icon: Package },
  { to: "/cars", label: "Car Hire", icon: Car },
];

const TRUST = [
  { icon: Clock, label: "Exclusive Last Minute deals" },
  { icon: ShieldCheck, label: "Secure booking" },
  { icon: Headphones, label: "Travel expert support" },
];

const CurrencySwitcher = ({ light }) => {
  const { code, current, list, setByCode } = useCurrency();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex items-center gap-1 font-semibold ${light ? "text-white/90 hover:text-white" : "text-slate-600 hover:text-slate-950"}`} data-testid="currency-switcher">
          {current.symbol} {code} <ChevronDown className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-y-auto">
        {list.map((c) => (
          <DropdownMenuItem key={c.code} onClick={() => setByCode(c.code)} data-testid={`currency-${c.code}`}
            className={code === c.code ? "bg-brand/10 text-brand font-bold" : ""}>
            <span className="w-8">{c.symbol}</span> {c.code} · {c.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Logo = () => (
  <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
    <img src="/wth-logo.png" alt="World Tour Hub Ltd" className="h-9 sm:h-10 w-auto object-contain" />
  </Link>
);

export const Navbar = () => {
  const { user, isAuthed, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50" data-testid="navbar">
      {/* Main magenta bar */}
      <div className="bg-brand" style={{ backgroundColor: "#E00080" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[68px]">
            <Logo />
            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) => {
                const active = location.pathname.startsWith(n.to);
                return (
                  <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${active ? "bg-white/20 text-white" : "text-white/85 hover:bg-white/10 hover:text-white"}`}>
                    <n.icon className="w-4 h-4" />{n.label}
                  </Link>
                );
              })}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-white/85 hover:bg-white/10 hover:text-white transition-colors" data-testid="nav-holidays">
                    <Package className="w-4 h-4" /> Holidays <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {COLLECTION_NAV.map(([to, label]) => (
                    <DropdownMenuItem key={to} onClick={() => navigate(to)} data-testid={`holiday-${to}`}>{label}</DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/destinations")}>Destination Guides</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
            <div className="hidden lg:flex items-center gap-4">
              <a href="tel:+442079460000" className="flex items-center gap-2 text-white">
                <Phone className="w-4 h-4" />
                <span className="leading-none"><span className="block text-[10px] text-white/70 uppercase tracking-wide">Call to book</span><span className="block font-heading font-bold text-sm">+44 20 7946 0000</span></span>
              </a>
              <div className="h-6 w-px bg-white/25" />
              <CurrencySwitcher light />
              {isAuthed ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="rounded-full bg-white text-brand hover:bg-white/90 gap-2 font-bold" data-testid="account-menu-btn">
                      <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5 text-xs text-slate-500 truncate">{user.email}</div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/my-bookings")} data-testid="menu-my-bookings"><Ticket className="w-4 h-4 mr-2" /> My Bookings</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/manage-booking")} data-testid="menu-manage-booking"><Ticket className="w-4 h-4 mr-2" /> Manage Booking</DropdownMenuItem>
                    {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button className="rounded-full bg-white text-brand hover:bg-white/90 gap-2 font-bold" onClick={() => navigate("/login")} data-testid="login-btn"><User className="w-4 h-4" /> Sign in</Button>
              )}
            </div>
            <button className="lg:hidden p-2 text-white" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="hidden md:block bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-11 flex items-center justify-center gap-0 divide-x divide-slate-200">
          {TRUST.map((t) => (
            <div key={t.label} className="flex items-center gap-2 px-8 text-slate-700">
              <t.icon className="w-4 h-4 text-brand" />
              <span className="text-sm font-semibold">{t.label}</span>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden border-b border-slate-200 bg-white">
            <div className="px-4 py-4 space-y-1">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-slate-700 font-semibold hover:bg-slate-100"><n.icon className="w-5 h-5 text-brand" />{n.label}</Link>
              ))}
              <Link to="/manage-booking" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-slate-700 font-semibold hover:bg-slate-100"><Ticket className="w-5 h-5 text-brand" /> Manage Booking</Link>
              <div className="px-3 py-2 flex items-center justify-between"><span className="text-sm text-slate-500 font-semibold">Currency</span><CurrencySwitcher /></div>
              <div className="pt-2 flex gap-2">
                {isAuthed ? (
                  <>
                    <Button className="flex-1 rounded-full bg-brand" onClick={() => { setOpen(false); navigate("/my-bookings"); }}>My Bookings</Button>
                    <Button variant="outline" className="rounded-full" onClick={logout}>Sign out</Button>
                  </>
                ) : <Button className="flex-1 rounded-full bg-brand" onClick={() => { setOpen(false); navigate("/login"); }}>Sign in</Button>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
